import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { CompanyService } from '../../services/company-service/company.service';
import { DriverService } from '../../services/driver-service/driver.service';
import { Any } from 'json2typescript';
import { QuickDialog } from '../../utilities/Dialog/QuickDialog';
import { AuthService } from '../../services/auth-service/auth.service';
import { CONSTANTS } from '../../utilities/Constants';

@Component({
  selector: 'app-driver-access-dialog',
  templateUrl: './driver-access-dialog.component.html',
  styleUrls: ['./driver-access-dialog.component.css']
})
export class DriverAccessDialogComponent implements OnInit {
  companies = [];
  allCompanies = [];
  clients = [];
  clientid = null;
  isSuper = false;
  driver;
  driverid;
  /** All clients linked to this driver (platform: GET /driver/providers). */
  assignedClients: { id: number; name: string }[] = [];
  /** Client whose company access / pay plan is shown below. */
  selectedClientId: number | null = null;
  clientsLoading = false;
  addClientPickerId: number | string = 'none';
  assignClientSubmitting = false;
  /** @deprecated use selectedClientId + assignedClients — kept for non-super fallbacks */
  assignedClientId: any = null;
  assignedClientName: string = null;
  /** When true, use portal-drivers company-location APIs with portalDriverId (numeric). */
  isPortalDriver = false;
  portalDriverId: number = null;
  
  // Pay Plan Settings
  selectedPayPlan: string = 'default';
  flatAmount: number = null;
  ratePercentage: number = null;
  constructor(private auth: AuthService, private dialog: MatDialog,
    public dialogRef: MatDialogRef<DriverAccessDialogComponent>, private driverService: DriverService, private companyService: CompanyService, private cdr: ChangeDetectorRef, @Inject(MAT_DIALOG_DATA) driver) { 

      this.driver = driver;
      this.isPortalDriver = !!(driver && driver.isPortalDriver && driver.portalDriverId != null);
      this.portalDriverId = this.isPortalDriver ? Number(driver.portalDriverId) : null;
      this.driverid = this.driver.driverid;
      this.assignedClientId = this.resolveDriverClientId(driver);
      this.assignedClientName = this.resolveDriverClientName(driver);
      if (this.isSuperUser() && this.assignedClientId != null && this.assignedClientId !== '') {
        this.clientid = this.assignedClientId;
      }
      if (this.isSuperUser() && (this.clientid == null || this.clientid === '')) {
        this.clientid = 'none';
      }
    }

  private getPlatformDriverId(): number | null {
    if (this.isPortalDriver) {
      return null;
    }
    const raw = this.driver && this.driver.driverid != null ? this.driver.driverid : this.driverid;
    const n = parseInt(String(raw).replace(/^portal_/i, ''), 10);
    return isNaN(n) ? null : n;
  }

  private normalizeProviderClientRow(row: any): { id: number; name: string } | null {
    if (!row || typeof row !== 'object') {
      return null;
    }
    const rawId = row.clientid != null ? row.clientid : row.id;
    if (rawId == null || rawId === '') {
      return null;
    }
    const id = parseInt(String(rawId), 10);
    if (isNaN(id)) {
      return null;
    }
    const nameRaw = row.name || row.client_name || row.clientName || row.clientname;
    const name = nameRaw != null && String(nameRaw).trim() !== ''
      ? String(nameRaw).trim()
      : `Client #${id}`;
    return { id, name };
  }

  getSelectedClientName(): string {
    if (!this.selectedClientId) {
      return '';
    }
    const match = this.assignedClients.find((c) => String(c.id) === String(this.selectedClientId));
    if (match) {
      return match.name;
    }
    if (this.assignedClientName) {
      return this.assignedClientName;
    }
    return `Client #${this.selectedClientId}`;
  }

  isSelectedClient(c: { id: number }): boolean {
    return c != null && this.selectedClientId != null && String(c.id) === String(this.selectedClientId);
  }

  selectClientForAccess(client: { id: number; name: string }) {
    if (!client) {
      return;
    }
    this.selectedClientId = client.id;
    this.clientid = client.id;
    this.assignedClientId = client.id;
    this.assignedClientName = client.name;
    this.loadDriversCompanies();
    if (!this.isPortalDriver) {
      this.loadDriverPayPlan();
    }
    this.cdr.detectChanges();
  }

  private syncSelectedClientAfterListChange() {
    if (!this.assignedClients.length) {
      this.selectedClientId = null;
      this.companies = [];
      this.allCompanies = [];
      if (this.isSuper) {
        this.clientid = 'none';
      }
      return;
    }
    let targetId = this.selectedClientId;
    if (targetId == null && this.assignedClientId != null && this.assignedClientId !== '') {
      const n = parseInt(String(this.assignedClientId), 10);
      targetId = isNaN(n) ? null : n;
    }
    if (targetId != null) {
      const match = this.assignedClients.find((c) => String(c.id) === String(targetId));
      if (match) {
        if (this.selectedClientId !== match.id) {
          this.selectClientForAccess(match);
        }
        return;
      }
    }
    this.selectClientForAccess(this.assignedClients[0]);
  }

  refreshAssignedClientsFromApi(): Promise<void> {
    this.clientsLoading = true;
    if (this.isPortalDriver) {
      return this.driverService.getPortalDrivers(true).then((res) => {
        const rows = this.driverService.mapPortalDriversResponse(res) || [];
        const match = rows.find(r =>
          Number(r.portalDriverId) === Number(this.portalDriverId) ||
          String(r.driverid) === String(this.driver.driverid)
        );
        this.assignedClients = [];
        if (match && match.clientid != null) {
          const id = parseInt(String(match.clientid), 10);
          if (!isNaN(id)) {
            const name = match.client_name || match.clientName
              || this.resolveDriverClientName(match)
              || `Client #${id}`;
            this.assignedClients = [{ id, name: String(name) }];
          }
        } else if (this.assignedClientId != null) {
          const id = parseInt(String(this.assignedClientId), 10);
          if (!isNaN(id)) {
            this.assignedClients = [{
              id,
              name: this.assignedClientName || `Client #${id}`
            }];
          }
        }
        this.syncAssignedClientNameFromList();
        this.syncSelectedClientAfterListChange();
      }).catch(() => {
        this.assignedClients = [];
      }).then(() => {
        this.clientsLoading = false;
        this.cdr.detectChanges();
      }) as Promise<void>;
    }

    const platformId = this.getPlatformDriverId();
    if (platformId == null) {
      this.assignedClients = [];
      this.clientsLoading = false;
      return Promise.resolve();
    }
    return this.driverService.getDriverProviders(platformId).then((res) => {
      const rows = (res && res.data) ? res.data : [];
      const mapped: { id: number; name: string }[] = [];
      const seen = new Set<string>();
      if (Array.isArray(rows)) {
        rows.forEach((row) => {
          const entry = this.normalizeProviderClientRow(row);
          if (entry && !seen.has(String(entry.id))) {
            seen.add(String(entry.id));
            mapped.push(entry);
          }
        });
      }
      this.assignedClients = mapped;
      this.syncAssignedClientNameFromList();
      this.syncSelectedClientAfterListChange();
    }).catch(() => {
      this.assignedClients = [];
    }).then(() => {
      this.clientsLoading = false;
      this.cdr.detectChanges();
    });
  }

  onSuperClientDropdownChange() {
    if (this.clientid == null || this.clientid === 'none' || this.clientid === '') {
      this.selectedClientId = null;
      this.companies = [];
      this.allCompanies = [];
      return;
    }
    const match = this.clients.find((c) =>
      String(c.id) === String(this.clientid) ||
      String(c.clientid) === String(this.clientid)
    );
    const name = match
      ? (match.name || match.client_name || match.clientName || `Client #${this.clientid}`)
      : `Client #${this.clientid}`;
    this.selectClientForAccess({
      id: parseInt(String(this.clientid), 10),
      name: String(name)
    });
  }

  canShowAddClientSection(): boolean {
    if (this.isPortalDriver) {
      return false;
    }
    if (this.isSuper) {
      return this.getClientsAvailableToAssign().length > 0 || this.assignedClients.length === 0;
    }
    const pid = this.getPlatformDriverId();
    if (pid == null) {
      return false;
    }
    const tokenClient = AuthService.getClientId();
    if (tokenClient == null || tokenClient === '') {
      return false;
    }
    const cid = parseInt(String(tokenClient), 10);
    if (isNaN(cid)) {
      return false;
    }
    return !this.assignedClients.some((c) => String(c.id) === String(cid));
  }

  getClientsAvailableToAssign(): { id: number; name: string }[] {
    const assignedIds = new Set(this.assignedClients.map((c) => String(c.id)));
    if (!this.isSuper) {
      const tokenClient = AuthService.getClientId();
      if (tokenClient == null || tokenClient === '') {
        return [];
      }
      const id = parseInt(String(tokenClient), 10);
      if (isNaN(id) || assignedIds.has(String(id))) {
        return [];
      }
      const name = this.assignedClientName || `Client #${id}`;
      return [{ id, name: String(name) }];
    }
    if (!this.clients || !this.clients.length) {
      return [];
    }
    const out: { id: number; name: string }[] = [];
    this.clients.forEach((cl) => {
      const rawId = cl.id != null ? cl.id : cl.clientid;
      if (rawId == null || rawId === '') {
        return;
      }
      const id = parseInt(String(rawId), 10);
      if (isNaN(id) || assignedIds.has(String(id))) {
        return;
      }
      const name = cl.name || cl.client_name || cl.clientName || `Client #${id}`;
      out.push({ id, name: String(name) });
    });
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }

  getAddClientSectionLabel(): string {
    return this.assignedClients.length === 0 ? 'Assign client' : 'Add another client';
  }

  assignDriverToNewClient() {
    if (this.assignClientSubmitting) {
      return;
    }
    const raw = this.addClientPickerId;
    if (raw == null || raw === 'none' || raw === '') {
      new QuickDialog(this.dialog).InfoDialog('Select a client to assign.', 'Choose client', 'OK');
      return;
    }
    const clientid = parseInt(String(raw), 10);
    if (isNaN(clientid)) {
      return;
    }
    const match = this.getClientsAvailableToAssign().find((c) => c.id === clientid)
      || this.clients.find((c) => parseInt(String(c.id != null ? c.id : c.clientid), 10) === clientid);
    const clientName = match
      ? (match.name || (match as any).client_name || `Client #${clientid}`)
      : `Client #${clientid}`;
    const driverName = `${this.driver.firstname || ''} ${this.driver.lastname || ''}`.trim() || 'This driver';
    const platformId = this.getPlatformDriverId();
    if (platformId == null) {
      new QuickDialog(this.dialog).InfoDialog('Could not resolve driver.', 'Cannot assign', 'OK');
      return;
    }

    new QuickDialog(this.dialog).DecisionDialog({
      message: `Assign ${driverName} to ${clientName}? You can then grant company access for that client.`,
      options: [
        { code: 'yes', message: 'Yes, assign', icon: 'fa-link' },
        { code: 'no', message: 'Cancel', icon: 'fa-ban' }
      ]
    }).then((choice) => {
      if (choice !== 'yes') {
        return;
      }
      this.assignClientSubmitting = true;
      const loading = new QuickDialog(this.dialog).LoadingDialog('Assigning client…');
      this.driverService.assignDriverToClient(platformId, clientid).then((res) => {
        loading.close();
        this.assignClientSubmitting = false;
        if (res && (res.status === true || res.status === 'true' || res.success)) {
          this.addClientPickerId = 'none';
          this.refreshAssignedClientsFromApi().then(() => {
            this.selectClientForAccess({ id: clientid, name: String(clientName) });
            new QuickDialog(this.dialog).InfoDialog(
              `${driverName} is now assigned to ${clientName}. Grant company access below if needed.`,
              'Client assigned',
              'OK'
            );
          });
        } else {
          const msg = res && res.message ? res.message : 'Could not assign this client.';
          new QuickDialog(this.dialog).InfoDialog(msg, 'Error', 'OK');
        }
      }).catch(() => {
        loading.close();
        this.assignClientSubmitting = false;
        new QuickDialog(this.dialog).InfoDialog('Could not assign this client.', 'Error', 'OK');
      });
    });
  }

  private resolveDriverClientId(driver: any): any {
    if (!driver) {
      return null;
    }
    if (driver.clientid != null && driver.clientid !== undefined && driver.clientid !== '') {
      return driver.clientid;
    }
    if (driver.client_id != null && driver.client_id !== undefined && driver.client_id !== '') {
      return driver.client_id;
    }
    if (!this.isSuperUser()) {
      return AuthService.getClientId();
    }
    return null;
  }

  private resolveDriverClientName(driver: any): string {
    if (!driver) {
      return null;
    }
    const candidates = [
      driver.client_name,
      driver.clientName,
      driver.clientname,
      driver.client && driver.client.name
    ];
    for (let i = 0; i < candidates.length; i++) {
      const value = candidates[i];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return null;
  }

  private isSuperUser(): boolean {
    return this.auth.hasOneOfRoles([CONSTANTS.ADMIN_TYPE_SUPER]);
  }

  getAssignedClientLabel(): string {
    if (this.assignedClientName) {
      return this.assignedClientName;
    }
    if (this.assignedClientId != null && this.assignedClientId !== '') {
      return `Client #${this.assignedClientId}`;
    }
    return null;
  }

  /** Client scope used for unassign (super may pick from dropdown; others use assigned / token client). */
  getEffectiveClientIdForUnassign(): number | null {
    if (this.isSuper) {
      const fromDropdown = this.clientid;
      if (fromDropdown != null && fromDropdown !== 'none' && fromDropdown !== '') {
        const n = parseInt(String(fromDropdown), 10);
        if (!isNaN(n)) {
          return n;
        }
      }
    }
    if (this.assignedClientId != null && this.assignedClientId !== '') {
      const n = parseInt(String(this.assignedClientId), 10);
      if (!isNaN(n)) {
        return n;
      }
    }
    const tokenClient = AuthService.getClientId();
    if (tokenClient != null && tokenClient !== '') {
      const n = parseInt(String(tokenClient), 10);
      if (!isNaN(n)) {
        return n;
      }
    }
    return null;
  }

  canUnassignFromClient(client?: { id: number; name?: string }): boolean {
    if (client) {
      if (this.isPortalDriver) {
        return this.portalDriverId != null && this.driver.portalActive !== false;
      }
      return this.getPlatformDriverId() != null && client.id != null;
    }
    if (!this.assignedClients.length && !this.getAssignedClientLabel()) {
      return false;
    }
    if (this.isPortalDriver) {
      return this.portalDriverId != null && this.driver.portalActive !== false;
    }
    return this.getPlatformDriverId() != null && this.getEffectiveClientIdForUnassign() != null;
  }

  unassignFromClient(event?: Event, client?: { id: number; name: string }) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const clientId = client != null ? client.id : this.getEffectiveClientIdForUnassign();
    const clientLabel = client != null
      ? (client.name || `Client #${client.id}`)
      : (this.getAssignedClientLabel() || (clientId != null ? `client #${clientId}` : 'this client'));
    const driverName = `${this.driver.firstname || ''} ${this.driver.lastname || ''}`.trim() || 'this driver';

    new QuickDialog(this.dialog).DecisionDialog({
      message: this.isPortalDriver
        ? `Remove ${driverName} from ${clientLabel}? They will no longer appear in this client's driver list.`
        : `Unassign ${driverName} from ${clientLabel}? Company access for this client will be removed.`,
      options: [
        { code: 'yes', message: 'Yes, unassign', icon: 'fa-unlink' },
        { code: 'no', message: 'Cancel', icon: 'fa-ban' }
      ]
    }).then((choice) => {
      if (choice !== 'yes') {
        return;
      }
      const loading = new QuickDialog(this.dialog).LoadingDialog('Unassigning from client...');
      const done = (ok: boolean, err?: any) => {
        loading.close();
        if (ok) {
          this.refreshAssignedClientsFromApi().then(() => {
            const remaining = this.assignedClients.length;
            const message = remaining > 0
              ? `${driverName} is no longer assigned to ${clientLabel}. Select another client below to continue managing access.`
              : `${driverName} is no longer assigned to ${clientLabel}.`;
            new QuickDialog(this.dialog).InfoDialog(message, 'Unassigned', 'OK').then(() => {
              if (remaining === 0) {
                this.assignedClientId = null;
                this.assignedClientName = null;
                this.dialogRef.close({ unassigned: true });
              }
            });
          });
        } else {
          const msg = err && err.message ? err.message : 'Could not unassign the driver from this client.';
          new QuickDialog(this.dialog).InfoDialog(msg, 'Error', 'OK');
        }
      };

      if (this.isPortalDriver && this.portalDriverId != null) {
        if (clientId == null) {
          loading.close();
          new QuickDialog(this.dialog).InfoDialog(
            this.isSuper ? 'Select the client to unassign from the dropdown first.' : 'Client could not be determined.',
            'Cannot unassign',
            'OK'
          );
          return;
        }
        this.driverService.unassignPortalDriverFromClient(this.portalDriverId, clientId)
          .then((res) => {
            if (res && (res.status === true || res.status === 'true' || res.success)) {
              done(true);
            } else {
              done(false);
            }
          })
          .catch(() => done(false));
        return;
      }

      if (clientId == null || this.getPlatformDriverId() == null) {
        loading.close();
        new QuickDialog(this.dialog).InfoDialog(
          this.isSuper ? 'Select the client to unassign from the list first.' : 'Client could not be determined.',
          'Cannot unassign',
          'OK'
        );
        return;
      }

      this.driverService.unassignDriverFromClient(this.getPlatformDriverId(), clientId)
        .then((res) => {
          if (res && (res.status === true || res.status === 'true' || res.success)) {
            done(true);
          } else {
            done(false);
          }
        })
        .catch(() => done(false));
    });
  }

  private syncAssignedClientNameFromList() {
    if (this.assignedClients.length && this.clients && this.clients.length) {
      this.assignedClients = this.assignedClients.map((c) => {
        const match = this.clients.find(cl =>
          String(cl.id) === String(c.id) ||
          String(cl.clientid) === String(c.id) ||
          String(cl.client_id) === String(c.id)
        );
        if (match) {
          const name = match.name || match.client_name || match.clientName;
          if (name != null && String(name).trim() !== '') {
            return { id: c.id, name: String(name).trim() };
          }
        }
        return c;
      });
    }
    if (this.assignedClientName || this.assignedClientId == null || this.assignedClientId === '') {
      return;
    }
    if (!this.clients || !this.clients.length) {
      return;
    }
    const match = this.clients.find(c =>
      String(c.id) === String(this.assignedClientId) ||
      String(c.clientid) === String(this.assignedClientId) ||
      String(c.client_id) === String(this.assignedClientId)
    );
    if (match) {
      this.assignedClientName = match.name || match.client_name || match.clientName || null;
    }
  }

  private applyAssignedClient(info: { id?: any; name?: string }) {
    if (!info) {
      return;
    }
    if (info.id != null && info.id !== '') {
      this.assignedClientId = info.id;
      if (this.isSuper && (this.clientid == null || this.clientid === 'none' || this.clientid === '')) {
        this.clientid = info.id;
      }
    }
    if (info.name && String(info.name).trim() !== '') {
      this.assignedClientName = String(info.name).trim();
    }
  }

  private extractClientFromPayload(res: any): { id?: any; name?: string } {
    if (!res) {
      return null;
    }
    const data = res.data != null ? res.data : res;
    const candidates = [];
    if (Array.isArray(data)) {
      data.forEach((item) => candidates.push(item));
    } else if (data) {
      candidates.push(data);
      if (data.client) {
        candidates.push(data.client);
      }
      if (Array.isArray(data.clients)) {
        data.clients.forEach((item) => candidates.push(item));
      }
      if (Array.isArray(data.companies)) {
        data.companies.forEach((item) => candidates.push(item));
      }
    }

    for (let i = 0; i < candidates.length; i++) {
      const item = candidates[i];
      if (!item || typeof item !== 'object') {
        continue;
      }
      const id = item.clientid != null ? item.clientid
        : (item.client_id != null ? item.client_id
          : (item.providerid != null ? item.providerid
            : (item.id != null && (item.name || item.client_name || item.clientName) ? item.id : null)));
      const name = item.client_name || item.clientName || item.clientname
        || item.client || item.name || null;
      if (id != null || name) {
        return { id, name: name ? String(name) : null };
      }
    }
    return null;
  }

  private finishAssignedClientResolve() {
    this.syncAssignedClientNameFromList();
    const afterName = (!this.assignedClientName && this.assignedClientId != null)
      ? this.resolveClientNameById(this.assignedClientId)
      : Promise.resolve();
    afterName.then(() => {
      if (this.clientid != null && this.clientid !== 'none' && this.clientid !== '') {
        this.loadDriversCompanies();
      }
      this.cdr.detectChanges();
    });
  }

  private fetchAssignedClientForSuper() {
    if (this.isPortalDriver) {
      // Portal drivers own clientid on portal_drivers; list may omit it — reload portals and match.
      this.driverService.getPortalDrivers(true).then((res) => {
        const rows = this.driverService.mapPortalDriversResponse(res) || [];
        const match = rows.find(r =>
          Number(r.portalDriverId) === Number(this.portalDriverId) ||
          String(r.driverid) === String(this.driver.driverid)
        );
        if (match && match.clientid != null) {
          this.applyAssignedClient({
            id: match.clientid,
            name: match.client_name || match.clientName || null
          });
        }
        this.finishAssignedClientResolve();
      }).catch(() => {
        this.cdr.detectChanges();
      });
      return;
    }

    // Platform drivers: GET /driver/providers?driverid= returns clients {id,name}
    this.driverService.getDriverProviders(this.driverid).then((res) => {
      const rows = (res && res.data) ? res.data : [];
      if (Array.isArray(rows) && rows.length) {
        const primary = rows[0];
        this.applyAssignedClient({
          id: primary.clientid != null ? primary.clientid : primary.id,
          name: primary.name || primary.client_name || null
        });
      }
      this.finishAssignedClientResolve();
    }).catch(() => {
      this.cdr.detectChanges();
    });
  }

  private resolveClientNameById(clientId: any): Promise<void> {
    return this.companyService.getClientById(clientId).then((res: any) => {
      const info = this.extractClientFromPayload(res);
      if (info && info.name) {
        this.assignedClientName = info.name;
      } else if (res && res.data && res.data.name) {
        this.assignedClientName = res.data.name;
      } else if (res && res.name) {
        this.assignedClientName = res.name;
      }
      this.cdr.detectChanges();
    }).catch(() => { /* keep id label */ });
  }

  ngOnInit() {
    const afterClientsLoaded = () => {
      return this.refreshAssignedClientsFromApi().then(() => {
        if (!this.assignedClients.length && this.assignedClientId != null && this.assignedClientId !== '') {
          const id = parseInt(String(this.assignedClientId), 10);
          if (!isNaN(id)) {
            this.selectClientForAccess({
              id,
              name: this.assignedClientName || `Client #${id}`
            });
          }
        } else if (!this.assignedClients.length) {
          this.loadDriversCompanies();
        }
        if (!this.isPortalDriver) {
          this.loadDriverPayPlan();
        }
        this.cdr.detectChanges();
      });
    };

    if (this.isSuperUser()) {
      this.isSuper = true;
      this.companyService.getClients().then((res) => {
        this.clients = (res && res.data) ? res.data : [];
        this.syncAssignedClientNameFromList();
        return afterClientsLoaded();
      }).catch(() => afterClientsLoaded());
    } else {
      if (this.assignedClientId != null && !this.assignedClientName) {
        this.companyService.getClients().then((res) => {
          this.clients = res.data || [];
          this.syncAssignedClientNameFromList();
          if (!this.assignedClientName) {
            this.resolveClientNameById(this.assignedClientId);
          }
          return afterClientsLoaded();
        }).catch(() => afterClientsLoaded());
      } else {
        afterClientsLoaded();
      }
    }
  }

  onClientSelected() {
    this.loadDriversCompanies();
    // Reload pay plan settings for the new client
    this.loadDriverPayPlan();
  }

  loadDriversCompanies() {
    if (this.isSuper && (this.clientid == null || this.clientid === 'none' || this.clientid === '')) {
      this.companies = [];
      this.allCompanies = [];
      return;
    }
    const loadAssigned = this.isPortalDriver
      ? this.driverService.getPortalDriverCompanyLocations(this.portalDriverId, this.clientid)
      : this.driverService.getCompany(this.driverid, this.clientid);
    loadAssigned.then((res: any) => {
      this.companies = res.data;
      this.loadAllCompanies();
    });
  }

  unassignCompanyLocation($event: Event, companyid, locationid) {
    const target = $event.target as HTMLInputElement;
    var d = new QuickDialog(this.dialog).LoadingDialog("Unassigning...");
    if (!target.checked) {
      const p = this.isPortalDriver
        ? this.driverService.unassignPortalCompanyLocation(this.portalDriverId, companyid, locationid, this.clientid)
        : this.driverService.unassignCompanyLocations(this.driverid, companyid, locationid, this.clientid);
      p.then(() => {
        d.close();
        this.loadDriversCompanies();
      }).catch(e => {
        d.close();
      });
    }
  }

  assignCompanyLocation($event: Event, companyid, locationid) {
    const target = $event.target as HTMLInputElement;
    var d = new QuickDialog(this.dialog).LoadingDialog("Assigning...");
    if (target.checked) {
      const p = this.isPortalDriver
        ? this.driverService.assignPortalCompanyLocation(this.portalDriverId, companyid, locationid, this.clientid)
        : this.driverService.assignCompanyLocation(this.driverid, companyid, locationid, this.clientid);
      p.then(() => {
        d.close();
        this.loadDriversCompanies();
      }).catch(e => {
        d.close();
      });
    }
  }

  alreadyAssigned(location) {
    var assiged = false;
    this.companies.forEach(c => {
      c.locations.forEach(l => {
        if (l.id == location.id) {
          assiged = true;
        }
      })
    });
    return assiged;
  }

  removeAlreadyAssigned() {
    this.companies.forEach(c => {
      var company = this.allCompanies.find(co => co.id == c.id);
      if(company){
        var fullAccess = company.locations.every(l => c.locations.find(x => x.id == l.id));
        if (fullAccess) {
          var index = this.allCompanies.indexOf(company);
          this.allCompanies.splice(index, 1);
        }
      }
    });
  }

  loadAllCompanies() {
    this.companyService.getCompany(this.clientid).then((res: any) => {
      this.allCompanies = res.data;
      this.removeAlreadyAssigned();
    });
  }

  loadDriverPayPlan() {
    if (this.isPortalDriver) {
      return;
    }
    if (!this.isBearerDriver()) {
      return;
    }
    
    this.driverService.getDriverPayPlan(this.driverid, this.clientid).then((res) => {
      if (res.status && res.data) {
        this.selectedPayPlan = res.data.pay_plan_type || 'default';
        // Convert string values to numbers for proper binding
        this.flatAmount = res.data.flat_amount ? parseFloat(res.data.flat_amount) : null;
        this.ratePercentage = res.data.rate_percentage ? parseFloat(res.data.rate_percentage) : null;      
        // Trigger change detection to update the UI
        this.cdr.detectChanges();
      }
    }).catch(error => {
      console.error('Error loading driver pay plan:', error);
      // Keep default values if loading fails
    });
  }

  savePayPlanSettings() {
    if (this.isPortalDriver) {
      return;
    }
    if (!this.isBearerDriver()) {
      new QuickDialog(this.dialog).InfoDialog('Invalid Driver Type', 'Pay plan settings are only available for BEARER drivers', 'OK');
      return;
    }
    
    // Validate inputs based on selected pay plan
    if (this.selectedPayPlan === 'flat' && (!this.flatAmount || this.flatAmount <= 0)) {
      new QuickDialog(this.dialog).InfoDialog('Invalid flat amount', 'Please enter a valid flat amount (whole number greater than 0)', 'OK');
      return;
    }
    
    if (this.selectedPayPlan === 'rate' && (!this.ratePercentage || this.ratePercentage <= 0 || this.ratePercentage > 100)) {
      new QuickDialog(this.dialog).InfoDialog('Invalid percentage', 'Please enter a valid percentage (between 1 and 100)', 'OK');
      return;
    }

    // Create pay plan data object
    const payPlanData = {
      driverId: this.driverid,
      payPlanType: this.selectedPayPlan,
      flatAmount: this.selectedPayPlan === 'flat' ? this.flatAmount : null,
      ratePercentage: this.selectedPayPlan === 'rate' ? this.ratePercentage : null,
      clientId: this.clientid
    };

    // Show loading dialog
    // var d = new QuickDialog(this.dialog).LoadingDialog("Saving pay plan settings...");
    
    // Call the actual API
    this.driverService.saveDriverPayPlan(payPlanData).then((res) => {
      // d.close();
      if (res.status) {
        // new QuickDialog(this.dialog).InfoDialog('Pay plan settings saved successfully', 'Success', 'OK');
        this.loadDriverPayPlan();
        this.close();
      }
    }).catch(error => {
      // d.close();
      new QuickDialog(this.dialog).InfoDialog('Error saving pay plan settings', 'Please try again', 'OK');
    });
  }

  close() {
    this.dialogRef.close();
  }

  /**
   * Validate that only numbers are entered in input fields
   */
  validateNumber(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    // Allow: backspace, delete, tab, escape, enter
    if ([46, 8, 9, 27, 13].indexOf(charCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (charCode === 65 && event.ctrlKey === true) ||
        (charCode === 67 && event.ctrlKey === true) ||
        (charCode === 86 && event.ctrlKey === true) ||
        (charCode === 88 && event.ctrlKey === true)) {
      return true;
    }
    // Ensure that it is a number and stop the keypress
    if ((charCode < 48 || charCode > 57) && charCode !== 46) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  /**
   * Check if the driver is a BEARER type
   */
  isBearerDriver(): boolean {
    var payload = this.auth.getAdminPayload();
    const userType = payload.type
    const adminType = userType.toLowerCase() || '';
    const isBearer = adminType === 'bearer';
    return isBearer
  }

}



// WEBPACK FOOTER //
// ./src/app/admin-dashboard/driver-access-dialog/driver-access-dialog.component.ts